import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ISharedServiceProviderService } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { IRecipientInfo, IRecipientInfoService } from 'recipient-info-service-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { TerminologySentenceBuilder } from 'terminology-lib';
import { UtilsService } from 'utils-service-lib';
import { runtimeEnvironment } from '../../../../environments/environment';

@Component({
    selector: 'app-edit-config',
    templateUrl: './edit-config.component.html',
    styleUrls: ['./edit-config.component.scss', './edit-config.web-app.component.scss', './edit-config.chrome-extension.component.scss'],
    animations: [
        trigger('transitionNewItemInOut', [
            transition(':enter', [style({ height: '0' }), animate('.15s ease-out', style({ height: '53px' }))]),
            transition(':leave', [style({ height: '53px' }), animate('.15s ease-out', style({ height: '0' }))]),
        ]),
        trigger('transitionNewRecipientInOut', [
            transition(':enter', [
                style({ maxHeight: '0' }),
                animate('.4s ease-out', style({
                    maxHeight: runtimeEnvironment.renderMode === 'web-app' ? '164px' : '420px',
                })),
            ]),
            transition(':leave', [
                style({
                    maxHeight: runtimeEnvironment.renderMode === 'web-app' ? '164px' : '420px',
                }),
                animate('.4s ease-out', style({ maxHeight: '0' })),
            ]),
        ]),
    ],
    standalone: false
})
export class EditConfigComponent implements OnInit {
  @ViewChildren('recipientEmailInput') recipientList: QueryList<ElementRef>; // TODO: Need to add the ROLE in, so this will need to change

  get recipientsFormArray(): UntypedFormArray {
    return this.eFormGroup.get('recipients') as UntypedFormArray;
  }

  @Input() namespace;
  @Input() eFormGroup: UntypedFormGroup;
  @Input() recipients;
  @Input() recipientInfoService: IRecipientInfoService;
  @Input() emailSubject: string;
  @Input() emailBody: string;
  @Input() provider: string;
  @Input() usingDocusignTemplate: boolean;

  renderMode = runtimeEnvironment.renderMode;

  addRecipientButtonLabel: string;
  existingRecipientText: string;
  regexp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  signatureEnabled = true;
  panel = 0;

  addPerson: boolean;
  addEntity: boolean;

  eSigConfigOpen: boolean;

  showRecipientRole: boolean;
  isRecipientRoleDisabled = true;
  private serviceProvider: ISharedServiceProviderService;

  constructor(
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private messageNotificationService: MessageNotificationService,
    private utilsService: UtilsService,
    private identityStorage: IdentityStorage
  ) {
    this.serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.identityStorage.get()?.namespace);
    this.eSigConfigOpen = this.renderMode === 'chrome-extension';
  }

  ngOnInit(): void {
    this.utilsService.chromeExtConfigPopupExpanded = false;
    this.showRecipientRole = this.shouldShowRecipientRole();

    const terminologyService = this.serviceProvider.getTerminologyService();
    this.addRecipientButtonLabel = new TerminologySentenceBuilder()
      .text('Add ')
      .anarthrous._.name._.recipientType.build(terminologyService);

    const formArray = this.eFormGroup.get('recipients') as UntypedFormArray;
    this.eFormGroup.get('emailSubject').setValue(this.emailSubject);
    this.eFormGroup.get('emailBody').setValue(this.emailBody);
    if (formArray.controls.length === 0 && this.recipients) {
      this.recipients.forEach((recipient) => {
        formArray.push(
          new UntypedFormGroup({
            name: new UntypedFormControl(recipient.name, Validators.required),
            email: new UntypedFormControl(recipient.email, [Validators.required, UtilsService.emailValidator()]),
            role: new UntypedFormControl({
              value: recipient.role,
              disabled: this.isRecipientRoleDisabled,
            }),
            editing: new UntypedFormControl(false), // TODO: Check about sending to server - can we exclude it?
          })
        );
      });
    }

    setTimeout((_) => {
      this.existingRecipientText = this.recipients ? `${this.recipients.length} existing` : ``;
    }, 50);

    this.recipientDetailValidation();
  }

  recipientDetailValidation(): void {
    if (!this.eFormGroup.valid) {
      this.eSigConfigOpen = true;
    }
    this.recipientsFormArray.controls.forEach((recipients, i) => {
      if (!recipients.valid) {
        this.toggleEditRecipient(i);
      }
    });
  }

  expandESigConfig(configOpen: boolean): void {
    if (!configOpen) {
      this.eSigConfigOpen = true;
    } else {
      this.eSigConfigOpen = false;
    }
  }

  setPanel(index: number) {
    this.panel = index;
    this.utilsService.chromeExtConfigPopupExpanded = true;
  }

  panelClosed(index: number): void {
    if (this.panel === index) {
      this.panel = 0;
      this.utilsService.chromeExtConfigPopupExpanded = false;
    }
  }

  focusPanel(elem: HTMLButtonElement | HTMLInputElement): void {
    elem.focus();
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.recipientsFormArray.controls, event.previousIndex, event.currentIndex);
  }

  removeRecipient(i: number) {
    this.recipientsFormArray.removeAt(i);
  }

  toggleEditRecipient(i: number): void {
    const editing = this.recipientsFormArray.controls[i].get('editing');
    editing.setValue(!editing.value);
  }

  addRecipient(recipient: IRecipientInfo): void {
    this.recipientsFormArray.push(
      new UntypedFormGroup({
        name: new UntypedFormControl(recipient.name, Validators.required),
        email: new UntypedFormControl(recipient.email, [Validators.required, UtilsService.emailValidator()]),
        role: new UntypedFormControl({
          value: recipient.role,
          disabled: this.isRecipientRoleDisabled,
        }),
        editing: new UntypedFormControl(recipient.editing),
      })
    );
    setTimeout(() => {
      this.recipientList.last?.nativeElement.focus();
    }, 100);
    this.addPerson = false;
    this.addEntity = false;
  }

  acceptRecipientEdits(i: number): void {
    const nameInput = this.getFormControl(i, 'name').value.trim();
    const emailInput = this.getFormControl(i, 'email').value.trim();
    const validEmail = this.regexp.test(emailInput);
    const validName = !nameInput;
    const nameErrorMsg = 'Recipient name must not be empty!';
    const emailErrorMsg = `Recipient email: ${emailInput}  is in wrong format, e.g. example@google.com`;
    console.log(this.getFormControl(i, 'email').value + ' is valid? ' + validEmail);
    if (validEmail && !validName) {
      this.getFormControl(i, 'name').setValue(nameInput);
      this.getFormControl(i, 'email').setValue(emailInput);
      this.getFormControl(i, 'editing').setValue(false);
    } else {
      this.messageNotificationService.error(validName ? nameErrorMsg : emailErrorMsg);
    }
  }

  getFormControl(i: number, controlName: string): any {
    return this.recipientsFormArray.controls[i].get(controlName);
  }

  addPersonButton(): void {
    if (this.addEntity) {
      this.addEntity = false;
      setTimeout((_) => {
        this.addPerson = true;
      }, 800);
    } else {
      this.addPerson = true;
    }
  }

  addEntityButton(): void {
    if (this.addPerson) {
      this.addPerson = false;
      setTimeout((_) => {
        this.addEntity = true;
      }, 800);
    } else {
      this.addEntity = true;
    }
  }

  private shouldShowRecipientRole() {
    if (this.provider === 'docusign') {
      // if using docusign template, show recipient role, otherwise hide it
      return this.usingDocusignTemplate;
    }
    return true;
  }
}
